import {
  TeamCalendarEntryType,
  type TeamCalendarEntry,
} from "./team-calendar-entry";

/**
 * How far either side of "now" an agent is given calendar entries as context.
 * Merchant-facing surfaces that claim the agent knows about an event must use
 * this same window, or they will promise awareness the agent does not have.
 */
export const TEAM_CALENDAR_AGENT_CONTEXT_WINDOW_DAYS = 7;

/** The most entries an agent is given at once, earliest start first. */
export const TEAM_CALENDAR_AGENT_CONTEXT_MAX_ENTRIES = 20;

export enum TeamCalendarEntryStatus {
  RecentlyEnded = "recently_ended",
  HappeningNow = "happening_now",
  Upcoming = "upcoming",
}

/**
 * Lifecycle is derived from (startAt, endAt, now), never stored, so every
 * surface computes it the same way.
 */
export function getTeamCalendarEntryStatus(
  entry: Pick<TeamCalendarEntry, "startAt" | "endAt">,
  now: Date,
): TeamCalendarEntryStatus {
  if (entry.endAt < now) {
    return TeamCalendarEntryStatus.RecentlyEnded;
  }
  if (entry.startAt > now) {
    return TeamCalendarEntryStatus.Upcoming;
  }
  return TeamCalendarEntryStatus.HappeningNow;
}

/**
 * Whether an entry reaches an agent's prompt at all. Canceled entries and
 * non-marketing types are dropped before the context is built.
 */
export function isTeamCalendarEntryVisibleToAgent(
  entry: Pick<TeamCalendarEntry, "canceledAt" | "type">,
): boolean {
  return !entry.canceledAt && entry.type === TeamCalendarEntryType.Marketing;
}

/**
 * Shift a date by whole UTC days.
 *
 * UTC on purpose: the agent's context window is computed this way, so any
 * surface claiming to show that window has to bucket identically. The
 * local-time `addDays` in redo/web/src/event-calendar/utils/date.ts is a
 * different function for a different job.
 */
export function addUtcDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}
